import React, {useEffect, useMemo, useState} from "react";
import {Button} from "antd";
import {CaretLeftOutlined, CaretRightOutlined} from "@ant-design/icons";
import {GetBookingsByCourseIdWithCourseLessonBookingsQuery} from "../../../generated/graphql";
import css from "./LessonBookings.less";
import {renderBookingNumber} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import {isBookingSettled} from "../../../helpers/bookingDetailsRouting";

export type SelectedBooking = {
    index: number;
    booking: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0];
};

// bookingsOfCourse?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"];

const useBookingSelector = (
    bookings?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"],
) => {
    const [selectedBooking, setSelectedBooking] = useState<SelectedBooking | null>(null);
    const [allBookings, setAllBookings] = useState<Array<SelectedBooking>>();
    const bookingCount = useMemo(() => bookings?.length, [bookings]);

    useEffect(() => {
        if (bookings) {
            setAllBookings(bookings.map((booking, index) => ({index, booking})));
            setSelectedBooking({index: 0, booking: bookings[0]});
        }
    }, [bookings]);

    return {selectedBooking, allBookings, setSelectedBooking, setAllBookings, bookingCount};
};

export default useBookingSelector;

export const BookingSelector = ({
    selectedBooking,
    allBookings,
    setSelectedBooking,
}: {
    selectedBooking: SelectedBooking | null;
    allBookings?: Array<SelectedBooking>;
    setSelectedBooking: (selectedBooking: SelectedBooking) => void;
}) => {
    if (!selectedBooking || !allBookings) {
        return null;
    }

    const handlePrevious = () => {
        if (selectedBooking.index > 0) {
            setSelectedBooking({
                index: selectedBooking.index - 1,
                booking: allBookings[selectedBooking.index - 1].booking,
            });
        }
    };

    const handleNext = () => {
        if (selectedBooking.index < allBookings.length - 1) {
            setSelectedBooking({
                index: selectedBooking.index + 1,
                booking: allBookings[selectedBooking.index + 1].booking,
            });
        }
    };

    const BookingNumber = () => {
        const {bookingNumber, bookingNumberPrefix, bookingType, canceled} = selectedBooking.booking;
        const isSettled = isBookingSettled(selectedBooking.booking);
        const style: React.CSSProperties = {
            fontSize: "1.3em",
            lineHeight: "1.3em",
            fontWeight: "bold",
            borderBottom: "none",
        };

        return (
            <>
                {renderBookingNumber({
                    bookingNumber,
                    bookingNumberPrefix,
                    bookingType,
                    canceled,
                    isSettled,
                    // className: css.header,
                    style,
                    hideBadge: true,
                })}
            </>
        );
    };

    return (
        <div className={css.bookingSelector}>
            <Button
                className={css.bookingSelectorButton}
                size="small"
                onClick={handlePrevious}
                disabled={selectedBooking.index === 0}
                data-testid="booking-selector-prev"
                // style={{paddingLeft: 0}}
            >
                <>
                    <CaretLeftOutlined style={{marginRight: 7, marginLeft: -7}} />
                    {"Vorherige Buchung"}
                </>
            </Button>
            <div className={css.bookingSelectorTitle}>
                <BookingNumber />
            </div>

            <Button
                className={css.bookingSelectorButton}
                size="small"
                onClick={handleNext}
                disabled={selectedBooking.index === allBookings.length - 1}
                data-testid="booking-selector-next"
                // style={{paddingRight: 0}}
            >
                <>
                    {"Nächste Buchung"}
                    <CaretRightOutlined style={{marginLeft: 7, marginRight: -7}} />
                </>
            </Button>
        </div>
    );
};
